/*
 * The values the patient site compares against, as enums (review MR !1 S38 / N1: no hard-coded string comparisons).
 *
 * ⚠ THE STRING TYPES IN membership.model.ts ARE DERIVED FROM THESE (`${MembershipStatusEnum}` and so on), so the two can
 * never disagree: a value added here is a value the type accepts, and a value removed here is a compile error wherever a
 * screen still uses it. The data the server sends keeps its plain-string shape, which is why fixtures and specs may still
 * write 'ACTIVE' where a field is typed with the derived union.
 */

/** Contract §2, `MEMBERSHIP_STATUS`. */
export enum MembershipStatusEnum {
  PendingPayment = 'PENDING_PAYMENT',
  Active = 'ACTIVE',
  Exhausted = 'EXHAUSTED',
  Expired = 'EXPIRED',
  Suspended = 'SUSPENDED',
  Cancelled = 'CANCELLED',
  Refunded = 'REFUNDED',
  /** A renewal bought early, waiting for the current membership to end (Builder-Growth §4.3). */
  Queued = 'QUEUED',
}

/**
 * Contract 2, `MembershipRedemption.entityType`. The radiology entity is `Radio`, NEVER `Radiology` (contract Correction 1).
 */
export enum MembershipEntityTypeEnum {
  Hospital = 'Hospital',
  Laboratory = 'Laboratory',
  Radio = 'Radio',
}

/** The Doctors tab's gender filter, as the server's Joi accepts it. */
export enum DoctorGenderEnum {
  Male = 'MALE',
  Female = 'FEMALE',
  Other = 'OTHER',
}

/** The four kinds of page Lifemaan hosts. The first segment of the address is its lower-case form. */
export enum MarketingProfileTypeEnum {
  Doctor = 'Doctor',
  Hospital = 'Hospital',
  Laboratory = 'Laboratory',
  Radio = 'Radio',
}

/** What `POST /public/purchase/initiate` answers. */
export enum PurchaseStatusEnum {
  PaymentNotConfigured = 'PAYMENT_NOT_CONFIGURED',
  PaymentPending = 'PAYMENT_PENDING',
  Activated = 'ACTIVATED',
}

/** What the server found when it asked Cashfree about an order (POST /public/payment/verify). */
export enum PaymentVerifyStatusEnum {
  Activated = 'ACTIVATED',
  Pending = 'PENDING',
  Failed = 'FAILED',
  Expired = 'EXPIRED',
  NeedsAttention = 'NEEDS_ATTENTION',
}

/** Which kind of order a payment or a coupon is for. */
export enum OrderKindEnum {
  Purchase = 'PURCHASE',
  Renewal = 'RENEWAL',
}

/** Cashfree's two environments; the server decides which one an order uses. */
export enum PaymentEnvironmentEnum {
  Sandbox = 'SANDBOX',
  Production = 'PRODUCTION',
}

/** Why a screen has nothing to show. */
export enum UnavailableReasonEnum {
  FeatureOff = 'FEATURE_OFF',
  Network = 'NETWORK',
  Server = 'SERVER',
}

/** What a successful referral gives the member who referred. */
export enum ReferralRewardTypeEnum {
  PurchaseCredit = 'PURCHASE_CREDIT',
  BenefitCredit = 'BENEFIT_CREDIT',
  PromoCredit = 'PROMO_CREDIT',
}

/** Where a referral credit can be spent. */
export enum ReferralCreditUseEnum {
  MembershipPurchaseAndRenewal = 'MEMBERSHIP_PURCHASE_AND_RENEWAL',
  MembershipPurchase = 'MEMBERSHIP_PURCHASE',
  MembershipRenewal = 'MEMBERSHIP_RENEWAL',
  /** The programme adds the reward straight onto the membership's benefit - nothing to spend later. */
  MembershipBenefit = 'MEMBERSHIP_BENEFIT',
  None = 'NONE',
}

/** When a renewal starts. */
export enum RenewalTimingEnum {
  Immediate = 'IMMEDIATE',
  Queued = 'QUEUED',
}

/** Where a legal document's text comes from. */
export enum LegalDocumentSourceEnum {
  Published = 'PUBLISHED',
  LegacyFile = 'LEGACY_FILE',
}

/** The redemption states the history screen draws differently (the field itself stays a string). */
export enum RedemptionStatusEnum {
  Confirmed = 'CONFIRMED',
  Reversed = 'REVERSED',
}

/** How a Find care or Doctors list is ordered. */
export enum FindSortEnum {
  Name = 'name',
  Rating = 'rating',
  Experience = 'experience',
}
